import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const NewLeadsConfigure = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin;

  const [allPositions, setAllPositions] = useState([]);
  const [includedPositions, setIncludedPositions] = useState([]);
  const [excludedLeadSources, setExcludedLeadSources] = useState('');
  const [limit, setLimit] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const normalizeSelectedPositions = (positions, priority = []) => {
    const selected = Array.isArray(positions) ? positions : [];
    const orderedPriority = Array.isArray(priority) ? priority.filter(position => selected.includes(position)) : [];

    return [
      ...orderedPriority,
      ...selected.filter(position => !orderedPriority.includes(position))
    ];
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [isSuperAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [positionsRes, configRes] = await Promise.all([
        axios.get('/api/settings/applicant-positions'),
        axios.get('/api/settings/new-leads-config')
      ]);
      setAllPositions(positionsRes.data);
      setIncludedPositions(normalizeSelectedPositions(
        configRes.data.includedPositions || [],
        configRes.data.positionPriority || []
      ));
      setExcludedLeadSources((configRes.data.excludedLeadSources || []).join(', '));
      setLimit(configRes.data.limit || 30);
    } catch (e) {
      console.error('Error fetching config:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePosition = (position) => {
    setIncludedPositions(prev =>
      prev.includes(position)
        ? prev.filter(p => p !== position)
        : [...prev, position]
    );
  };

  const handleMovePosition = (position, direction) => {
    setIncludedPositions(prev => {
      const index = prev.indexOf(position);
      const nextIndex = index + direction;

      if (index === -1 || nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
      return updated;
    });
  };

  const handleSelectAll = () => {
    setIncludedPositions([...allPositions]);
  };

  const handleDeselectAll = () => {
    setIncludedPositions([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        includedPositions,
        positionPriority: includedPositions,
        excludedLeadSources: excludedLeadSources
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
        limit
      };
      const res = await axios.put('/api/settings/new-leads-config', payload);
      setIncludedPositions(normalizeSelectedPositions(
        res.data.includedPositions || [],
        res.data.positionPriority || []
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert(e.response?.data?.message || 'Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Layout>
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6 text-red-600">
            Access denied. Super admin only.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Leads Page Configuration</h1>
            <p className="text-sm text-gray-500 mt-1">Configure which leads appear on the New Leads page (applies to all users)</p>
          </div>
          <Link
            to="/settings"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ← Back to Settings
          </Link>
        </div>

        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">Loading configuration...</div>
        ) : (
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            {/* Positions to Show */}
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-900">Positions to Show</label>
                  <p className="text-xs text-gray-500">Check the positions you want to prioritize, then arrange them by priority. Other positions will fill any remaining lead slots. If none are selected, all positions will be shown by newest lead first.</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-xs text-gray-400">|</span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              {allPositions.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No positions found in the database.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3">
                  {allPositions.map((position) => (
                    <label
                      key={position}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={includedPositions.includes(position)}
                        onChange={() => handleTogglePosition(position)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 truncate" title={position}>{position}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                {includedPositions.length} of {allPositions.length} positions selected
              </p>

              {includedPositions.length > 0 && (
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-900">Position Priority</p>
                    <p className="text-xs text-gray-500">Top positions appear first, then other leads fill the limit</p>
                  </div>
                  <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                    {includedPositions.map((position, index) => (
                      <div key={position} className="flex items-center justify-between px-3 py-2">
                        <div className="min-w-0 flex items-center space-x-3">
                          <span className="w-6 text-xs font-medium text-gray-500">{index + 1}</span>
                          <span className="text-sm text-gray-800 truncate" title={position}>{position}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => handleMovePosition(position, -1)}
                            disabled={index === 0}
                            title="Move up"
                            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Move {position} up</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMovePosition(position, 1)}
                            disabled={index === includedPositions.length - 1}
                            title="Move down"
                            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Move {position} down</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Excluded Lead Sources */}
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-900 mb-1">Excluded Lead Sources</label>
              <p className="text-xs text-gray-500 mb-2">Leads from these sources will be hidden. Separate multiple values with commas.</p>
              <input
                type="text"
                value={excludedLeadSources}
                onChange={(e) => setExcludedLeadSources(e.target.value)}
                placeholder="e.g. Referral, Walk-in"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Max Leads */}
            <div className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Max Leads Displayed</p>
                <p className="text-xs text-gray-500">Maximum number of leads shown on the page</p>
              </div>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Save */}
            <div className="px-6 py-4 flex justify-end items-center space-x-3">
              {saved && <span className="text-sm text-green-600">Configuration saved!</span>}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NewLeadsConfigure;
